/**
 * Управление админ-сессией на экране карты (Требования 7.1–7.5, 10.4).
 *
 * Экран карты по умолчанию доступен только для просмотра (Требование 7.4).
 * Администратор может войти теми же учётными данными, что и в `/admin`
 * (HTTP Basic auth); наличие действующей сессии проверяется пробным запросом к
 * защищённому эндпоинту `/api/mapsadmin/*`, как это делает `admin.html`. При
 * наличии сессии страница включает инструменты рисования и размещения меток,
 * а сохранение выполняется с заголовком `Authorization` (Требования 7.1, 7.5,
 * 10.1, 10.4). Выход очищает сохранённые учётные данные (Требование 7.3).
 *
 * Архитектура «тонкий DOM, чистое ядро»: {@link AdminSession} не зависит от DOM
 * и WebGL — хранилище учётных данных и функция загрузки внедряются, поэтому
 * логику проверки/входа/выхода можно покрыть юнит-тестами. Значение по умолчанию
 * для хранилища — `localStorage`, для загрузки — глобальный `fetch`.
 *
 * Требования: 7.1, 7.2, 7.3, 7.4, 7.5, 10.4
 */

/**
 * Минимальный интерфейс key-value хранилища учётных данных (совместим с
 * `Window.localStorage`/`sessionStorage`). Внедряется для тестируемости.
 */
export interface CredentialStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

/** Ключ по умолчанию для хранения заголовка Basic-авторизации карт-админки. */
export const DEFAULT_AUTH_STORAGE_KEY = 'mapsAdminAuth';

/** Путь по умолчанию к защищённому эндпоинту для проверки сессии. */
export const DEFAULT_PROBE_PATH = '/api/mapsadmin/maps';

/**
 * Закодировать строку в Base64 (UTF-8). Работает и в браузере (`btoa`), и в
 * Node (`Buffer`). Используется для формирования токена Basic-авторизации.
 */
function base64Encode(input: string): string {
  const bytes = new TextEncoder().encode(input);
  let binary = '';
  for (const b of bytes) {
    binary += String.fromCharCode(b);
  }
  const g = globalThis as unknown as {
    btoa?: (s: string) => string;
    Buffer?: { from(s: string, enc: string): { toString(enc: string): string } };
  };
  if (typeof g.btoa === 'function') {
    return g.btoa(binary);
  }
  if (g.Buffer) {
    return g.Buffer.from(input, 'utf-8').toString('base64');
  }
  throw new Error('Не найден кодировщик Base64 в текущем окружении');
}

/**
 * Сформировать значение заголовка `Authorization` для HTTP Basic auth из логина
 * и пароля: `"Basic base64(username:password)"`.
 *
 * @param username логин администратора
 * @param password пароль администратора
 */
export function buildBasicAuthHeader(username: string, password: string): string {
  return 'Basic ' + base64Encode(`${username}:${password}`);
}

/**
 * Построить объект заголовков с `Authorization`, если задан заголовок; иначе —
 * пустой объект. Удобно расширять им заголовки запроса на сохранение.
 *
 * @param header значение заголовка `Authorization` либо `null`
 */
export function authHeaders(header: string | null): Record<string, string> {
  return header ? { Authorization: header } : {};
}

/** Опции конфигурации {@link AdminSession}. */
export interface AdminSessionOptions {
  /** Базовый URL API (без завершающего слэша). По умолчанию пустая строка. */
  apiBase?: string;
  /** Функция загрузки (внедрение для тестов). По умолчанию глобальный `fetch`. */
  fetchFn?: typeof fetch;
  /**
   * Хранилище учётных данных. По умолчанию — `localStorage` (если доступен).
   * Передайте `null`, чтобы отключить персистентность (сессия только в памяти).
   */
  storage?: CredentialStorage | null;
  /** Ключ в хранилище для заголовка авторизации. */
  storageKey?: string;
  /** Путь к защищённому эндпоинту для проверки сессии. */
  probePath?: string;
}

/**
 * Разрешить хранилище по умолчанию: `localStorage`, если он доступен, иначе `null`.
 */
function defaultStorage(): CredentialStorage | null {
  try {
    const g = globalThis as unknown as { localStorage?: CredentialStorage };
    return g.localStorage ?? null;
  } catch {
    // Доступ к localStorage может бросать (например, в приватном режиме).
    return null;
  }
}

/**
 * Админ-сессия экрана карты: хранит заголовок Basic-авторизации, проверяет его
 * действительность пробным запросом к защищённому эндпоинту и предоставляет
 * заголовки для запросов на сохранение.
 *
 * Жизненный цикл:
 * 1. `new AdminSession(options)` — восстанавливает сохранённый заголовок из
 *    хранилища (если есть).
 * 2. `await probe()` — проверяет действительность текущего заголовка
 *    (Требование 7.4): `200` → сессия активна, `401` → нет (недействительный
 *    заголовок очищается).
 * 3. `await login(user, pass)` — формирует заголовок и проверяет его; при успехе
 *    сохраняет в хранилище (Требование 7.1).
 * 4. `logout()` — очищает заголовок и хранилище (Требование 7.3).
 */
export class AdminSession {
  private readonly apiBase: string;
  private readonly fetchFn: typeof fetch;
  private readonly storage: CredentialStorage | null;
  private readonly storageKey: string;
  private readonly probePath: string;

  /** Текущий заголовок `Authorization` (или `null`, если не задан). */
  private header: string | null = null;
  /** Подтверждена ли сессия последней успешной проверкой/входом. */
  private authenticated = false;

  constructor(options: AdminSessionOptions = {}) {
    this.apiBase = options.apiBase ?? '';
    this.fetchFn =
      options.fetchFn ??
      (typeof fetch !== 'undefined' ? fetch.bind(globalThis) : (undefined as never));
    this.storage =
      options.storage === undefined ? defaultStorage() : options.storage;
    this.storageKey = options.storageKey ?? DEFAULT_AUTH_STORAGE_KEY;
    this.probePath = options.probePath ?? DEFAULT_PROBE_PATH;

    // Восстановить сохранённый заголовок (сессия могла быть открыта ранее).
    const stored = this.storage?.getItem(this.storageKey) ?? null;
    if (stored) {
      this.header = stored;
    }
  }

  /** Текущий заголовок `Authorization` либо `null`. */
  getAuthHeader(): string | null {
    return this.header;
  }

  /**
   * Подтверждена ли сессия (последняя проверка/вход прошли успешно). Не выполняет
   * сетевых запросов — отражает результат последнего {@link probe}/{@link login}.
   */
  isAuthenticated(): boolean {
    return this.authenticated && this.header !== null;
  }

  /**
   * Заголовки для запроса на сохранение: `{ Authorization }`, если сессия задана,
   * иначе пустой объект. Запросы без валидной авторизации отклоняются сервером
   * (Требование 7.5, 10.4).
   */
  authHeaders(): Record<string, string> {
    return authHeaders(this.header);
  }

  /**
   * Установить заголовок авторизации напрямую (без проверки и сохранения).
   * Сбрасывает признак подтверждённости — вызовите {@link probe} для проверки.
   */
  setAuthHeader(header: string | null): void {
    this.header = header;
    this.authenticated = false;
  }

  /**
   * Проверить действительность текущего заголовка пробным запросом к защищённому
   * эндпоинту (Требование 7.4). Возвращает `true` при `2xx`. При `401`
   * недействительный заголовок очищается (в т.ч. из хранилища), возвращается
   * `false`. Сетевые ошибки трактуются как отсутствие сессии (`false`).
   *
   * @returns признак наличия действующей админ-сессии
   */
  async probe(): Promise<boolean> {
    if (!this.header) {
      this.authenticated = false;
      return false;
    }
    try {
      const response = await this.fetchFn(`${this.apiBase}${this.probePath}`, {
        headers: this.authHeaders(),
      });
      if (response.ok) {
        this.authenticated = true;
        return true;
      }
      if (response.status === 401 || response.status === 403) {
        // Недействительные учётные данные — очистить, чтобы не переспрашивать.
        this.clearStoredHeader();
      }
      this.authenticated = false;
      return false;
    } catch {
      this.authenticated = false;
      return false;
    }
  }

  /**
   * Войти под учётными данными администратора (Требование 7.1). Формирует
   * заголовок Basic-авторизации, проверяет его {@link probe}; при успехе
   * сохраняет заголовок в хранилище и возвращает `true`. При неудаче заголовок
   * очищается и возвращается `false`.
   *
   * @param username логин администратора
   * @param password пароль администратора
   */
  async login(username: string, password: string): Promise<boolean> {
    this.header = buildBasicAuthHeader(username, password);
    this.authenticated = false;
    const ok = await this.probe();
    if (ok) {
      this.storage?.setItem(this.storageKey, this.header);
      return true;
    }
    this.clearStoredHeader();
    return false;
  }

  /**
   * Завершить админ-сессию (Требование 7.3): очистить заголовок авторизации и
   * удалить его из хранилища.
   */
  logout(): void {
    this.clearStoredHeader();
  }

  /** Очистить заголовок из памяти и хранилища, сбросить признак подтверждённости. */
  private clearStoredHeader(): void {
    this.header = null;
    this.authenticated = false;
    this.storage?.removeItem(this.storageKey);
  }
}
